import React from "react";
import { Box, Typography, Divider, Paper, Stack, Grid } from "@mui/material";
import AppModal from "../common/AppModal";

const ViewDocumentHeaderModal = ({ open, onClose, documentHeader }) => {
  if (!documentHeader) return null;

  const { companySnapshot } = documentHeader;
  const meta = documentHeader.templateMetadata || {};
  const theme = meta.theme || {
    primaryColor: "#003366",
    secondaryColor: "#f8f9fa",
    showLogo: true,
  };
  const labels = meta.labels || { documentTitle: "DOCUMENT", taxName: "Tax" };
  const toggles = meta.toggles || {
    format: "A4",
    showTaxColumn: true,
    showBankDetails: true,
  };
  const footer = meta.footer || {};

  const isThermal = toggles.format?.startsWith("THERMAL");
  const is58mm = toggles.format === "THERMAL_58MM";

  // --- RENDERING FOR THERMAL PRINTERS ---
  const renderThermalMock = () => (
    <Paper
      elevation={3}
      sx={{
        width: is58mm ? "250px" : "320px", // Exact visual representations
        mx: "auto",
        p: 2,
        bgcolor: "#fefefe",
        color: "#000",
        fontFamily: "'Courier New', Courier, monospace", // Thermal fonts are usually monospace
      }}
    >
      <Box textAlign="center" mb={1}>
        {theme.showLogo && companySnapshot?.logoUrl && (
          <img
            src={companySnapshot.logoUrl}
            alt="Logo"
            style={{
              maxHeight: "40px",
              maxWidth: "150px",
              objectFit: "contain",
              filter: "grayscale(100%)",
              marginBottom: "5px",
            }}
          />
        )}
        <Typography variant="body1" fontWeight="bold">
          {companySnapshot?.companyName}
        </Typography>
        <Typography variant="caption" display="block">
          {companySnapshot?.address}
        </Typography>
        <Typography variant="caption" display="block">
          Tel: {companySnapshot?.phone}
        </Typography>
        <Typography variant="caption" display="block">
          PIN: {companySnapshot?.taxId}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        display="block"
        sx={{ borderBottom: "1px dashed #000", mb: 1 }}
      />

      <Typography variant="body2" fontWeight="bold">
        {labels.documentTitle} #: MOCK-001
      </Typography>
      <Typography variant="caption" display="block">
        Date: {new Date().toLocaleDateString()}
      </Typography>
      <Typography variant="caption" display="block">
        Cust: John Doe
      </Typography>

      <Typography
        variant="caption"
        display="block"
        sx={{ borderBottom: "1px dashed #000", mt: 1, mb: 1 }}
      />

      {/* Items */}
      <Typography variant="caption" fontWeight="bold" display="block">
        Premium Service Consultation
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption">1 x 5,000</Typography>
        <Typography variant="caption">5,000</Typography>
      </Box>

      <Typography
        variant="caption"
        display="block"
        sx={{ borderBottom: "1px dashed #000", mt: 1, mb: 1 }}
      />

      {/* Totals */}
      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption">Subtotal</Typography>
        <Typography variant="caption">5,000</Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body2" fontWeight="bold">
          TOTAL
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          5,000
        </Typography>
      </Box>

      <Typography
        variant="caption"
        display="block"
        sx={{ borderBottom: "1px dashed #000", mt: 1, mb: 1 }}
      />
      <Typography
        variant="caption"
        fontWeight="bold"
        display="block"
        textAlign="center"
        mt={2}
      >
        {footer.thankYouMessage}
      </Typography>
    </Paper>
  );

  // --- RENDERING FOR A4 PRINTERS ---
  const renderA4Mock = () => (
    <Paper
      elevation={3}
      sx={{
        maxWidth: "800px",
        mx: "auto",
        p: 5,
        bgcolor: "#ffffff",
        color: "#000000",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={4}
      >
        <Box>
          {theme.showLogo && companySnapshot?.logoUrl ? (
            <img
              src={companySnapshot.logoUrl}
              alt="Logo"
              style={{ maxHeight: "60px", marginBottom: "10px" }}
            />
          ) : (
            theme.showLogo && (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: theme.secondaryColor,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  LOGO
                </Typography>
              </Box>
            )
          )}
          <Typography variant="h6" fontWeight="bold">
            {companySnapshot?.companyName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {companySnapshot?.address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tel: {companySnapshot?.phone}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: {companySnapshot?.email}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="h4" fontWeight="bold" color={theme.primaryColor}>
            {labels.documentTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Doc #: <b>MOCK-001</b>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: <b>{new Date().toLocaleDateString()}</b>
          </Typography>
        </Box>
      </Stack>

      {/* Items Table Mock */}
      <Box sx={{ border: "1px solid #e0e0e0", mb: 4 }}>
        <Stack
          direction="row"
          sx={{ bgcolor: theme.primaryColor, color: "#fff", p: 1 }}
        >
          <Box flex={4}>
            <Typography variant="caption" fontWeight="bold">
              Item Description
            </Typography>
          </Box>
          <Box flex={1} textAlign="center">
            <Typography variant="caption" fontWeight="bold">
              Qty
            </Typography>
          </Box>
          <Box flex={1.5} textAlign="right">
            <Typography variant="caption" fontWeight="bold">
              Total
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
          <Box flex={4}>
            <Typography variant="body2">
              Premium Service Consultation
            </Typography>
          </Box>
          <Box flex={1} textAlign="center">
            <Typography variant="body2">1</Typography>
          </Box>
          <Box flex={1.5} textAlign="right">
            <Typography variant="body2" fontWeight="bold">
              5,000
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Footer Mock */}
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="body2"
        fontWeight="bold"
        color={theme.primaryColor}
        fontStyle="italic"
        textAlign="center"
      >
        {footer.thankYouMessage}
      </Typography>
    </Paper>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`${documentHeader.documentType.replace(/_/g, " ")} Theme Preview`}
      width="lg"
    >
      <Box sx={{ bgcolor: "#f5f5f5", p: 3, borderRadius: 2 }}>
        <Typography textAlign="center" color="text.secondary" mb={2}>
          {isThermal
            ? `Previewing as ${is58mm ? "58mm" : "80mm"} Thermal Receipt`
            : "Previewing as A4 Document"}
        </Typography>
        {isThermal ? renderThermalMock() : renderA4Mock()}
      </Box>
    </AppModal>
  );
};

export default ViewDocumentHeaderModal;
